import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { buildSchoolThemeCss, SCHOOL_THEME } from '../features/branding/palette.js';

const ThemeContext = createContext();

const SCHOOL_STYLE_ID = 'swan-school-theme';

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [themeSettings, setThemeSettings] = useState(() => {
    const saved = localStorage.getItem('themeSettings');
    return saved ? JSON.parse(saved) : {
      themeMode: 'light',
      lightTheme: 'light',
      darkTheme: 'dark',
      language: 'en',
      timezone: 'UTC'
    };
  });

  // School white-label override: derived {light,dark} token maps, or null.
  // Set by SchoolBrandingController (which lives inside SchoolProvider, below
  // this provider in the tree). When present, the active theme maps to the
  // school's themes and the whole app recolours.
  const [brandTheme, setBrandThemeState] = useState(null);

  // DaisyUI light themes
  const lightThemes = [
    'light', 'cupcake', 'bumblebee', 'emerald', 'corporate', 
    'retro', 'cyberpunk', 'valentine', 'garden', 'aqua', 
    'lofi', 'pastel', 'fantasy', 'wireframe'
  ];

  // DaisyUI dark themes
  const darkThemes = [
    'dark', 'synthwave', 'halloween', 'forest', 'black', 
    'luxury', 'dracula', 'night', 'coffee'
  ];

  // Apply theme to document
  const applyTheme = (themeName) => {
    document.documentElement.setAttribute('data-theme', themeName);
    localStorage.setItem('theme', themeName);
  };

  // Get current theme based on mode. When a school brand theme is active it wins
  // over the user's chosen daisyUI theme, but still honours the light/dark toggle.
  const getCurrentTheme = useCallback(() => {
    const mode = themeSettings.themeMode === 'dark' ? 'dark' : 'light';
    if (brandTheme) return SCHOOL_THEME[mode];
    return mode === 'light' ? themeSettings.lightTheme : themeSettings.darkTheme;
  }, [themeSettings.themeMode, themeSettings.lightTheme, themeSettings.darkTheme, brandTheme]);

  // Register/replace/remove the school theme's <style> as the override changes.
  const setBrandTheme = useCallback((derived) => {
    setBrandThemeState(derived || null);
    let el = document.getElementById(SCHOOL_STYLE_ID);
    if (!derived) {
      if (el) el.remove();
      return;
    }
    if (!el) {
      el = document.createElement('style');
      el.id = SCHOOL_STYLE_ID;
      document.head.appendChild(el);
    }
    el.textContent = buildSchoolThemeCss(derived);
  }, []);

  // Handle theme mode change (light/dark toggle)
  const handleThemeModeChange = (mode) => {
    const newSettings = { ...themeSettings, themeMode: mode };
    setThemeSettings(newSettings);
    const newTheme = mode === 'light' ? newSettings.lightTheme : newSettings.darkTheme;
    applyTheme(newTheme);
    localStorage.setItem('themeSettings', JSON.stringify(newSettings));
  };

  // Handle specific theme selection
  const handleThemeChange = (themeName) => {
    const isLightTheme = lightThemes.includes(themeName);
    const newSettings = {
      ...themeSettings,
      [isLightTheme ? 'lightTheme' : 'darkTheme']: themeName
    };
    setThemeSettings(newSettings);
    applyTheme(themeName);
    localStorage.setItem('themeSettings', JSON.stringify(newSettings));
  };

  // Initialize theme on component mount and whenever settings change
  useEffect(() => {
    const currentTheme = getCurrentTheme();
    applyTheme(currentTheme);
  }, [themeSettings, getCurrentTheme]);

  const value = {
    themeSettings,
    setThemeSettings,
    lightThemes,
    darkThemes,
    applyTheme,
    getCurrentTheme,
    handleThemeModeChange,
    handleThemeChange,
    brandTheme,
    setBrandTheme,
    isBranded: !!brandTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};